import {useCallback} from "react";
import ReduxFetchState from "redux-fetch-state";
import {put, takeEvery} from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import {useAppDispatch, useAppSelector} from "../../../../Hooks/useStore";
import {getCurrentLanguage} from "../../../../Utils/sagaHelpers";
import {alert} from "../../../../Utils/toast";

export type GetSubscriptionHistoryLoadAction = {
    type: string;
    payload: Record<string, never>;
};

export type GetSubscriptionHistoryData = {
    data: {
        userPlans: {
            id: number;
            status: string;
            wallet_id: null | number;
            activated_at: string;
            expired_at: string;
            num: string;
            for_months: number;
            sub_service_title: string;
            wallet?: {
                id: number;
                user_id: number;
                currency_id: number;
                amount: string;
                type: string;
                order_id: null;
                tracking_code: string;
            };
            session_tokens: {
                user_subscription_id: number;
                token_sum: string;
            }[]
        }[];
    };
};

const GetSubscriptionHistory = new ReduxFetchState<
    GetSubscriptionHistoryData | null,
    Record<string, never>,
    any
>("getSubscriptionHistory");

export function* watchGetSubscriptionHistory(_: GetSubscriptionHistoryLoadAction) {
    try {
        const lang: "fa" | "en" = yield getCurrentLanguage();

        const data: GetSubscriptionHistoryData = yield axios({
            url: "/api/v1/ai/subscription/history",
            method: "GET",
            headers: {
                lang,
            },
        });

        yield put(GetSubscriptionHistory.actions.loadSuccess({data: data.data}));
    } catch (e: any) {
        alert({
            message: e?.response?.data?.error ?? "Something Went Wrong!",
            type: "error",
        });
        yield put(GetSubscriptionHistory.actions.loadFailure(e?.response?.data));
    }
}

export function* getSubscriptionHistorySagas() {
    yield takeEvery(GetSubscriptionHistory.actionTypes.load, watchGetSubscriptionHistory);
}

export function useGetSubscriptionHistory() {
    const {loading, data, ...getSubscriptionHistoryState} = useAppSelector(
        (state) => state.getSubscriptionHistory
    );
    const dispatch = useAppDispatch();

    const dispatchGetSubscriptionHistory = useCallback(() => {
        dispatch(GetSubscriptionHistory.actions.load({}));
    }, [dispatch]);

    return {
        ...getSubscriptionHistoryState,
        dispatchGetSubscriptionHistory,
        getSubscriptionHistoryLoading: loading,
        getSubscriptionHistoryData: data,
    };
}

export const {
    actionTypes: getSubscriptionHistoryActionsTypes,
    actions: getSubscriptionHistoryActions,
    reducer: getSubscriptionHistoryReducer,
} = GetSubscriptionHistory;